import PageHeader from '../../components/PageHeader';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, IconButton, Slider, Grid,
  TextField, Button, Chip, Paper, Stack, Alert, CircularProgress,
  ToggleButtonGroup, ToggleButton, Tooltip, Divider, useTheme,
  alpha, Autocomplete, List, ListItem, ListItemText, ListItemIcon,
  Collapse,
} from '@mui/material';
import {
  PlayArrow, Pause, Stop, Speed as SpeedIcon, Timeline as TimelineIcon,
  DirectionsCar, Brightness4, Brightness7, CalendarMonth, Route as RouteIcon,
  AccessTime, Search as SearchIcon, LocalParking, Warning,
  ChevronRight, ExpandMore, ExpandLess,
} from '@mui/icons-material';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ReferenceLine, ResponsiveContainer, ReferenceDot,
} from 'recharts';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import api from '../../services/api';
import { onFlatMapStyleReady } from '../../utils/mapStyle';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PlaybackPoint {
  timestamp: string;
  latitude: number;
  longitude: number;
  speed: number;
  heading: number;
  ignition: boolean;
  odometer: number;
  satellites?: number;
  altitude?: number;
  movement?: boolean;
}

interface StopEvent {
  startTime: string;
  endTime: string;
  duration: number;
  latitude: number;
  longitude: number;
  pointIndex: number;
}

interface SpeedingEvent {
  startTime: string;
  endTime: string;
  duration: number;
  maxSpeed: number;
  latitude: number;
  longitude: number;
  pointIndex: number;
}

interface TripStats {
  duration: number;
  distance: number;
  maxSpeed: number;
  avgSpeed: number;
  pointCount: number;
}

interface TripSegment {
  segmentNumber: number;
  startTime: string;
  endTime: string;
  startLocation: { latitude: number; longitude: number };
  endLocation: { latitude: number; longitude: number };
  points: PlaybackPoint[];
  stats: TripStats;
  stops: StopEvent[];
  speedingEvents: SpeedingEvent[];
}

interface PlaybackData {
  device: { imei: string; plateNumber: string; make: string; modelYear: string };
  timeRange: { start: string; end: string };
  points: PlaybackPoint[];
  segments: TripSegment[];
  stats: TripStats;
}

interface Vehicle {
  _id: string;
  plateNumber: string;
  make: string;
  model: string;
  year: number;
  vin?: string;
  imei?: string;
  deviceBinding?: { currentDeviceId: string; isDeviceBound?: boolean };
}

// ─── Speed Graph Tooltip ──────────────────────────────────────────────────────

const SpeedGraphTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const speed = payload[0]?.value ?? 0;
  const color = speed > 80 ? '#f44336' : speed > 60 ? '#ff9800' : speed > 30 ? '#fbc02d' : '#00e676';
  return (
    <Paper elevation={6} sx={{
      p: 1.5,
      bgcolor: 'rgba(10,10,20,0.92)',
      border: `1px solid ${color}`,
      borderRadius: 2,
      backdropFilter: 'blur(8px)',
    }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" fontWeight="bold" sx={{ color }}>
        {Math.round(speed)} km/h
      </Typography>
    </Paper>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const TripPlaybackPage: React.FC = () => {
  const theme = useTheme();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const carMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const stopMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const speedMarkersRef = useRef<mapboxgl.Marker[]>([]);

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);

  const [imei, setImei] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [mapStyle, setMapStyle] = useState<'light' | 'dark'>('dark');
  const [mapLoaded, setMapLoaded] = useState(false);

  const [playbackData, setPlaybackData] = useState<PlaybackData | null>(null);
  const [activeSegment, setActiveSegment] = useState<TripSegment | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(10);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const [segmentPanelOpen, setSegmentPanelOpen] = useState(true);
  const [eventPanelOpen, setEventPanelOpen] = useState(true);

  const currentPoints = activeSegment?.points || [];
  const currentPoint = currentPoints[currentIndex];

  // ── Speed graph data (max 300 pts for performance) ────────────────────────
  const speedGraphData = useMemo(() => {
    if (!activeSegment?.points?.length) return [];
    const pts = activeSegment.points;
    const step = pts.length > 300 ? Math.floor(pts.length / 300) : 1;
    return pts
      .filter((_, i) => i % step === 0 || i === pts.length - 1)
      .map((p, chartIdx) => ({
        pointIndex: Math.min(chartIdx * step, pts.length - 1),
        time: new Date(p.timestamp).toLocaleTimeString('en-GB', {
          hour: '2-digit', minute: '2-digit',
        }),
        speed: Math.round(p.speed),
      }));
  }, [activeSegment]);

  const maxGraphSpeed = useMemo(() => {
    if (!speedGraphData.length) return 120;
    return Math.max(...speedGraphData.map(d => d.speed), 100);
  }, [speedGraphData]);

  // Chart index closest to current playback position
  const chartActiveIndex = useMemo(() => {
    if (!speedGraphData.length) return 0;
    let closest = 0;
    let minDiff = Infinity;
    speedGraphData.forEach((d, i) => {
      const diff = Math.abs(d.pointIndex - currentIndex);
      if (diff < minDiff) { minDiff = diff; closest = i; }
    });
    return closest;
  }, [currentIndex, speedGraphData]);

  // ── Fetch vehicles ─────────────────────────────────────────────────────────
  const fetchVehicles = async () => {
    try {
      setVehiclesLoading(true);
      const [vRes, dRes] = await Promise.all([
        api.get('/vehicles', { params: { limit: 1000 } }),
        api.get('/devices', { params: { page: 1, limit: 1000 } }),
      ]);
      const allVehicles: any[] = vRes.data.data || [];
      const allDevices: any[] = dRes.data.data || [];

      const deviceImeiMap = new Map<string, string>();
      allDevices.forEach((d: any) => {
        if (d._id && d.imei) deviceImeiMap.set(d._id.toString(), d.imei);
      });

      const deviceByVehicleId = new Map<string, string>();
      allDevices.forEach((d: any) => {
        if (d.vehicleId && d.imei) {
          const vid = typeof d.vehicleId === 'object'
            ? d.vehicleId._id?.toString() : d.vehicleId?.toString();
          if (vid) deviceByVehicleId.set(vid, d.imei);
        }
      });

      const valid: Vehicle[] = [];
      allVehicles.forEach((v: any) => {
        let res = v.deviceBinding?.currentDeviceId
          ? deviceImeiMap.get(v.deviceBinding.currentDeviceId.toString()) : undefined;
        if (!res) res = deviceByVehicleId.get(v._id.toString());
        if (!res && v.imei) res = v.imei;
        if (res) valid.push({ ...v, imei: res });
      });
      setVehicles(valid);
    } catch (err: any) {
      setError(`Failed to load vehicles: ${err.response?.data?.message || err.message}`);
    } finally {
      setVehiclesLoading(false);
    }
  };

  useEffect(() => { fetchVehicles(); }, []);

  useEffect(() => {
    const now = new Date();
    const ago = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    setEndTime(now.toISOString().slice(0, 16));
    setStartTime(ago.toISOString().slice(0, 16));
  }, []);

  // ── Map sources/layers helper ──────────────────────────────────────────────
  const addMapSources = (map: mapboxgl.Map) => {
    ['route-line', 'segment-line', 'trail-line'].forEach(id => {
      if (!map.getSource(id)) {
        map.addSource(id, {
          type: 'geojson',
          data: { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [] } },
        });
      }
    });

    const layers: any[] = [
      { id: 'route-background', source: 'route-line',   color: '#555',    width: 3,  opacity: 0.3, dash: [3, 3] },
      { id: 'segment-solid',    source: 'segment-line', color: '#2196f3', width: 4,  opacity: 0.5 },
      { id: 'trail-glow',       source: 'trail-line',   color: '#00ff00', width: 16, opacity: 0.4, blur: 8 },
      { id: 'trail-solid',      source: 'trail-line',   color: '#00ff00', width: 6,  opacity: 1.0 },
    ];

    layers.forEach(({ id, source, color, width, opacity, dash, blur }) => {
      if (!map.getLayer(id)) {
        const paint: any = { 'line-color': color, 'line-width': width, 'line-opacity': opacity };
        if (dash) paint['line-dasharray'] = dash;
        if (blur) paint['line-blur'] = blur;
        map.addLayer({ id, type: 'line', source, layout: { 'line-join': 'round', 'line-cap': 'round' }, paint });
      }
    });
  };

  // ── Map init ───────────────────────────────────────────────────────────────
  useEffect(() => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN ||
      'pk.eyJ1Ijoic29waGllMDEiLCJhIjoiY21jbmZtaHBkMDNhMjJuczMxd3dmeWVobCJ9.7W1F9G-KWZRqsiNPEle4sA';

    if (mapRef.current || !mapContainerRef.current) return;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/navigation-night-v1',
      center: [3.3792, 6.5244],
      zoom: 12,
    });
    mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    mapRef.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');
    const map = mapRef.current;
    const removeStyleListener = onFlatMapStyleReady(map, () => {
      if (mapRef.current !== map) return;
      addMapSources(map);
      setMapLoaded(true);
    });

    return () => {
      removeStyleListener();
      clearStopMarkers(); clearSpeedMarkers();
      carMarkerRef.current?.remove();
      mapRef.current?.remove(); mapRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMapStyle = () => {
    if (!mapRef.current || !mapLoaded) return;
    const map = mapRef.current;
    const newStyle = mapStyle === 'dark' ? 'light' : 'dark';
    setMapStyle(newStyle);
    setMapLoaded(false);
    map.once('style.load', () => {
      if (mapRef.current !== map) return;
      if (activeSegment) renderSegmentOnMap(activeSegment);
    });
    map.setStyle(newStyle === 'dark'
      ? 'mapbox://styles/mapbox/navigation-night-v1'
      : 'mapbox://styles/mapbox/streets-v12', { diff: false });
  };

  // ── Marker helpers ─────────────────────────────────────────────────────────
  const clearStopMarkers  = () => { stopMarkersRef.current.forEach(m => m.remove());  stopMarkersRef.current  = []; };
  const clearSpeedMarkers = () => { speedMarkersRef.current.forEach(m => m.remove()); speedMarkersRef.current = []; };

  const addStopMarkers = useCallback((stops: StopEvent[]) => {
    clearStopMarkers();
    if (!mapRef.current) return;
    stops.forEach((stop, idx) => {
      const el = document.createElement('div');
      const mins = Math.floor(stop.duration / 60);
      const secs = stop.duration % 60;
      el.innerHTML = `<div style="background:#ff9800;border:2px solid white;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.5);font-size:13px;cursor:pointer;">🅿️</div>`;
      const popup = new mapboxgl.Popup({ offset: 16, className: 'trip-event-popup' }).setHTML(`
        <div style="padding:10px;font-family:system-ui;min-width:160px;">
          <div style="font-weight:bold;color:#ff9800;margin-bottom:6px;">🅿️ Stop #${idx + 1}</div>
          <div style="font-size:12px;line-height:1.8;">
            <div><strong>Duration:</strong> ${mins > 0 ? `${mins}m ` : ''}${secs}s</div>
            <div><strong>From:</strong> ${new Date(stop.startTime).toLocaleTimeString()}</div>
            <div><strong>To:</strong> ${new Date(stop.endTime).toLocaleTimeString()}</div>
          </div>
        </div>`);
      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([stop.longitude, stop.latitude]).setPopup(popup).addTo(mapRef.current!);
      stopMarkersRef.current.push(marker);
    });
  }, []);

  const addSpeedingMarkers = useCallback((events: SpeedingEvent[]) => {
    clearSpeedMarkers();
    if (!mapRef.current) return;
    events.forEach((ev, idx) => {
      const el = document.createElement('div');
      el.innerHTML = `<div style="background:#f44336;border:2px solid white;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(244,67,54,0.6);font-size:10px;font-weight:bold;color:white;cursor:pointer;">${Math.round(ev.maxSpeed)}</div>`;
      const popup = new mapboxgl.Popup({ offset: 16, className: 'trip-event-popup' }).setHTML(`
        <div style="padding:10px;font-family:system-ui;min-width:160px;">
          <div style="font-weight:bold;color:#f44336;margin-bottom:6px;">⚡ Violation #${idx + 1}</div>
          <div style="font-size:12px;line-height:1.8;">
            <div><strong>Max Speed:</strong> ${ev.maxSpeed} km/h</div>
            <div><strong>Duration:</strong> ${ev.duration}s</div>
            <div><strong>At:</strong> ${new Date(ev.startTime).toLocaleTimeString()}</div>
          </div>
        </div>`);
      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([ev.longitude, ev.latitude]).setPopup(popup).addTo(mapRef.current!);
      speedMarkersRef.current.push(marker);
    });
  }, []);

  // ── Render segment on map ──────────────────────────────────────────────────
  const renderSegmentOnMap = useCallback((segment: TripSegment) => {
    if (!mapRef.current || !mapLoaded) return;
    const segCoords = segment.points.map(p => [p.longitude, p.latitude]);

    const segSrc = mapRef.current.getSource('segment-line') as mapboxgl.GeoJSONSource;
    if (segSrc) segSrc.setData({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: segCoords } });

    const trailSrc = mapRef.current.getSource('trail-line') as mapboxgl.GeoJSONSource;
    if (trailSrc) trailSrc.setData({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [] } });

    addStopMarkers(segment.stops);
    addSpeedingMarkers(segment.speedingEvents);

    const fp = segment.points[0];
    if (carMarkerRef.current) carMarkerRef.current.remove();
    const el = createCarMarkerElement(fp.speed, fp.heading, fp.ignition);
    carMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat([fp.longitude, fp.latitude]).addTo(mapRef.current!);

    const bounds = new mapboxgl.LngLatBounds();
    segCoords.forEach(c => bounds.extend(c as [number, number]));
    mapRef.current.fitBounds(bounds, { padding: 60, duration: 1000 });
  }, [mapLoaded, addStopMarkers, addSpeedingMarkers]);

  // ── Load trip data ─────────────────────────────────────────────────────────
  const loadPlaybackData = async () => {
    if (!imei || !startTime || !endTime) { setError('Please fill in all fields'); return; }
    setLoading(true); setError(''); pausePlayback(); setCurrentIndex(0);
    setActiveSegment(null); clearStopMarkers(); clearSpeedMarkers();

    try {
      const response = await api.get(`/telemetry/playback/${imei}`, {
        params: {
          startTime: new Date(startTime).toISOString(),
          endTime: new Date(endTime).toISOString(),
        },
      });

      const data: PlaybackData = response.data.data;

      // ✅ Safety net — older backend without segmentation
      if (!data.segments) data.segments = [];

      if (data.points?.length > 0) {
        data.points.sort((a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      }

      setPlaybackData(data);

      if (data.points.length > 0 && mapRef.current) {
        const allCoords = data.points.map(p => [p.longitude, p.latitude]);
        const src = mapRef.current.getSource('route-line') as mapboxgl.GeoJSONSource;
        if (src) src.setData({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: allCoords } });
      }

      if (data.segments?.length > 0) {
        selectSegment(data.segments[0]);
      } else if (data.points.length === 0) {
        setError('No GPS data found for this time range');
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load playback data');
    } finally {
      setLoading(false);
    }
  };

  const selectSegment = (segment: TripSegment) => {
    pausePlayback(); setActiveSegment(segment); setCurrentIndex(0);
    renderSegmentOnMap(segment);
  };

  // ── Playback engine ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentPoints.length || !mapRef.current || !currentPoint) return;
    if (carMarkerRef.current) {
      carMarkerRef.current.setLngLat([currentPoint.longitude, currentPoint.latitude]);
      const el = createCarMarkerElement(currentPoint.speed, currentPoint.heading, currentPoint.ignition);
      carMarkerRef.current.getElement().innerHTML = el.innerHTML;
      mapRef.current.easeTo({ center: [currentPoint.longitude, currentPoint.latitude], duration: 400 });
    }
    const traveled = currentPoints.slice(0, currentIndex + 1).map(p => [p.longitude, p.latitude]);
    const trailSrc = mapRef.current.getSource('trail-line') as mapboxgl.GeoJSONSource;
    if (trailSrc && traveled.length >= 2) {
      trailSrc.setData({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: traveled } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, activeSegment]);

  const startPlayback = () => {
    if (!currentPoints.length) return;
    setIsPlaying(true);
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => {
        if (prev >= currentPoints.length - 1) { stopPlayback(); return prev; }
        return prev + 1;
      });
    }, 1000 / playbackSpeed);
  };

  const pausePlayback = () => {
    setIsPlaying(false);
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  };

  const stopPlayback = () => { pausePlayback(); setCurrentIndex(0); };

  const handleSpeedChange = (newSpeed: number) => {
    setPlaybackSpeed(newSpeed);
    if (isPlaying) { pausePlayback(); setTimeout(() => startPlayback(), 100); }
  };

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  // ── Click on speed graph → jump playback ──────────────────────────────────
  const handleChartClick = (data: any) => {
    if (!data?.activePayload?.length) return;
    const graphPt = speedGraphData[data.activeTooltipIndex ?? 0];
    if (graphPt) { pausePlayback(); setCurrentIndex(graphPt.pointIndex); }
  };

  // ── Car SVG marker ─────────────────────────────────────────────────────────
  const createCarMarkerElement = (speed: number, heading: number, ignition: boolean) => {
    const el = document.createElement('div');
    el.style.width = '70px'; el.style.height = '70px'; el.style.cursor = 'pointer';
    const c = speed > 80 ? '#ff1744' : speed > 60 ? '#ff9800' : speed > 30 ? '#fbc02d' : '#00e676';
    el.innerHTML = `
      <svg width="70" height="70" viewBox="0 0 70 70" style="transform:rotate(${heading}deg);filter:drop-shadow(0 8px 16px rgba(0,0,0,0.6));">
        <g transform="translate(35,35)">
          <circle cx="0" cy="0" r="28" fill="${c}" opacity="0.15"/>
          <circle cx="0" cy="0" r="24" fill="${c}" opacity="0.25"/>
          <defs><linearGradient id="cg${heading}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:${c};stop-opacity:1"/>
            <stop offset="100%" style="stop-color:${c};stop-opacity:0.7"/>
          </linearGradient></defs>
          <path d="M-18,-9 L-18,-13 L-12,-17 L12,-17 L18,-13 L18,-9 L18,10 L12,13 L-12,13 L-18,10 Z" fill="url(#cg${heading})" stroke="white" stroke-width="2.5"/>
          <path d="M-12,-14 L-8,-17 L8,-17 L12,-14 L12,-8 L-12,-8 Z" fill="rgba(135,206,250,0.8)" stroke="white" stroke-width="2"/>
          <circle cx="-14" cy="10"  r="5.5" fill="#2c2c2c" stroke="#fff" stroke-width="2"/>
          <circle cx="14"  cy="10"  r="5.5" fill="#2c2c2c" stroke="#fff" stroke-width="2"/>
          <circle cx="-14" cy="-10" r="5.5" fill="#2c2c2c" stroke="#fff" stroke-width="2"/>
          <circle cx="14"  cy="-10" r="5.5" fill="#2c2c2c" stroke="#fff" stroke-width="2"/>
          ${ignition ? `<circle cx="0" cy="-14" r="4" fill="#00e676" stroke="white" stroke-width="2"><animate attributeName="opacity" values="0.9;0.5;0.9" dur="2s" repeatCount="indefinite"/></circle>` : ''}
        </g>
      </svg>`;
    return el;
  };

  // ── Formatters ─────────────────────────────────────────────────────────────
  const fmtDuration = (s: number) => { const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); return h > 0 ? `${h}h ${m}m` : `${m}m`; };
  const fmtTime    = (ts: string) => new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const fmtFull    = (ts: string) => { const d = new Date(ts); return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB')}`; };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{
      '& .trip-event-popup .mapboxgl-popup-content': {
        bgcolor: 'background.paper', color: 'text.primary', borderRadius: 2,
        boxShadow: 6, border: '1px solid', borderColor: 'divider',
      },
      '& .trip-event-popup .mapboxgl-popup-close-button': {
        color: 'text.primary', fontSize: 20, width: 28, height: 28,
      },
      '& .trip-event-popup.mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip, & .trip-event-popup.mapboxgl-popup-anchor-bottom-left .mapboxgl-popup-tip, & .trip-event-popup.mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip': { borderTopColor: 'background.paper' },
      '& .trip-event-popup.mapboxgl-popup-anchor-top .mapboxgl-popup-tip, & .trip-event-popup.mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip, & .trip-event-popup.mapboxgl-popup-anchor-top-right .mapboxgl-popup-tip': { borderBottomColor: 'background.paper' },
      '& .trip-event-popup.mapboxgl-popup-anchor-left .mapboxgl-popup-tip': { borderRightColor: 'background.paper' },
      '& .trip-event-popup.mapboxgl-popup-anchor-right .mapboxgl-popup-tip': { borderLeftColor: 'background.paper' },
    }}>

      {/* Header */}
      <PageHeader title="Trip Playback" description="Replay and analyze vehicle journeys" icon="trips" actions={<>
<Tooltip title={`Switch to ${mapStyle === 'dark' ? 'light' : 'dark'} mode`}>
          <IconButton onClick={toggleMapStyle} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)' }}>
            {mapStyle === 'dark' ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
        </Tooltip>
</>} />

      {/* Input Controls */}
      <Card sx={{ mb: 2, boxShadow: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} lg={3}>
              <Autocomplete
                options={vehicles} loading={vehiclesLoading} value={selectedVehicle}
                onChange={(_, v) => { setSelectedVehicle(v); if (v?.imei) { setImei(v.imei); setError(''); } }}
                getOptionLabel={(v) => `${v.plateNumber} - ${v.make} ${v.model}`}
                renderOption={(props, v) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="body2" fontWeight="bold">{v.plateNumber}</Typography>
                      <Typography variant="caption" color="text.secondary">{v.make} {v.model} ({v.year})</Typography>
                      {v.imei && <Typography variant="caption" color="primary">IMEI: {v.imei}</Typography>}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Select Vehicle" placeholder="Search by plate, make, or model"
                    InputProps={{ ...params.InputProps, startAdornment: <><SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />{params.InputProps.startAdornment}</> }}
                  />
                )}
                noOptionsText={vehiclesLoading ? 'Loading...' : 'No vehicles with devices found'}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <TextField fullWidth label="Device IMEI (Manual)" value={imei}
                onChange={(e) => setImei(e.target.value)} placeholder="Or enter IMEI manually"
                InputProps={{ startAdornment: <DirectionsCar sx={{ mr: 1, color: 'text.secondary' }} /> }}
                helperText="Auto-filled when you select a vehicle"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <TextField fullWidth type="datetime-local" label="Start Time" value={startTime}
                onChange={(e) => setStartTime(e.target.value)} InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: <CalendarMonth sx={{ mr: 1, color: 'text.secondary' }} /> }}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <TextField fullWidth type="datetime-local" label="End Time" value={endTime}
                onChange={(e) => setEndTime(e.target.value)} InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: <AccessTime sx={{ mr: 1, color: 'text.secondary' }} /> }}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <Button fullWidth variant="contained" onClick={loadPlaybackData} disabled={loading}
                size="large" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RouteIcon />}
                sx={{ height: 56, fontWeight: 'bold', boxShadow: 3 }}>
                {loading ? 'Loading...' : 'Load Trip'}
              </Button>
            </Grid>
          </Grid>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </CardContent>
      </Card>

      {/* Three-panel row */}
      {playbackData && (
        <Grid container spacing={2} sx={{ mb: 2 }}>

          {/* Segment Selector */}
          <Grid item xs={12} md={4}>
            <Card sx={{ boxShadow: 3, height: '100%' }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{
                  p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  borderBottom: '1px solid', borderColor: 'divider',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RouteIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="bold">Trip Segments</Typography>
                    <Chip size="small" label={playbackData.segments.length} color="primary" />
                  </Box>
                  <IconButton size="small" onClick={() => setSegmentPanelOpen(p => !p)}>
                    {segmentPanelOpen ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={segmentPanelOpen}>
                  {playbackData.segments.length === 0
                    ? <Box sx={{ p: 3, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No segments detected</Typography></Box>
                    : (
                      <List disablePadding sx={{ maxHeight: 320, overflow: 'auto' }}>
                        {playbackData.segments.map((seg) => {
                          const isActive = activeSegment?.segmentNumber === seg.segmentNumber;
                          return (
                            // ✅ FIX: removed 'button' prop — use onClick + sx cursor instead
                            <ListItem key={seg.segmentNumber} onClick={() => selectSegment(seg)} sx={{
                              cursor: 'pointer',
                              borderLeft: isActive ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
                              bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                              transition: 'all 0.2s',
                              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                              borderBottom: '1px solid', borderColor: 'divider',
                            }}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Box sx={{
                                  width: 28, height: 28, borderRadius: '50%',
                                  bgcolor: isActive ? 'primary.main' : alpha(theme.palette.primary.main, 0.2),
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  color: isActive ? 'white' : 'primary.main', fontSize: 12, fontWeight: 'bold',
                                }}>{seg.segmentNumber}</Box>
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" fontWeight={isActive ? 'bold' : 'normal'}>
                                      {fmtTime(seg.startTime)} → {fmtTime(seg.endTime)}
                                    </Typography>
                                    <ChevronRight fontSize="small" sx={{ color: 'text.secondary' }} />
                                  </Box>
                                }
                                secondary={
                                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                    <Chip size="small" label={`${seg.stats.distance}km`} sx={{ height: 18, fontSize: 10 }} />
                                    <Chip size="small" label={fmtDuration(seg.stats.duration)} sx={{ height: 18, fontSize: 10 }} />
                                    <Chip size="small" label={`${seg.stats.maxSpeed}km/h`}
                                      color={seg.stats.maxSpeed > 80 ? 'error' : 'default'} sx={{ height: 18, fontSize: 10 }} />
                                    {seg.stops.length > 0 && (
                                      <Chip size="small" icon={<LocalParking sx={{ fontSize: '10px !important' }} />}
                                        label={seg.stops.length} color="warning" sx={{ height: 18, fontSize: 10 }} />
                                    )}
                                    {seg.speedingEvents.length > 0 && (
                                      <Chip size="small" icon={<Warning sx={{ fontSize: '10px !important' }} />}
                                        label={seg.speedingEvents.length} color="error" sx={{ height: 18, fontSize: 10 }} />
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                </Collapse>
              </CardContent>
            </Card>
          </Grid>

          {/* Events Panel */}
          <Grid item xs={12} md={4}>
            <Card sx={{ boxShadow: 3, height: '100%' }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{
                  p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  bgcolor: alpha(theme.palette.warning.main, 0.08),
                  borderBottom: '1px solid', borderColor: 'divider',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning color="warning" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="bold">Events</Typography>
                    {activeSegment && (
                      <Chip size="small"
                        label={`${activeSegment.stops.length + activeSegment.speedingEvents.length} total`}
                        color="warning" />
                    )}
                  </Box>
                  <IconButton size="small" onClick={() => setEventPanelOpen(p => !p)}>
                    {eventPanelOpen ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={eventPanelOpen}>
                  {!activeSegment
                    ? <Box sx={{ p: 3, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">Select a segment to see events</Typography></Box>
                    : (
                      <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
                        {activeSegment.stops.map((stop, idx) => (
                          <Box key={`stop-${idx}`} onClick={() => {
                            setCurrentIndex(stop.pointIndex);
                            mapRef.current?.flyTo({ center: [stop.longitude, stop.latitude], zoom: 16, duration: 800 });
                          }} sx={{
                            p: 1.5, borderBottom: '1px solid', borderColor: 'divider',
                            display: 'flex', alignItems: 'flex-start', gap: 1.5, cursor: 'pointer',
                            '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.05) },
                          }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#ff9800', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 14 }}>🅿️</Box>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                Stop #{idx + 1} — {Math.floor(stop.duration / 60)}m {stop.duration % 60}s
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {fmtTime(stop.startTime)} → {fmtTime(stop.endTime)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                        {activeSegment.speedingEvents.map((ev, idx) => (
                          <Box key={`speed-${idx}`} onClick={() => {
                            setCurrentIndex(ev.pointIndex);
                            mapRef.current?.flyTo({ center: [ev.longitude, ev.latitude], zoom: 16, duration: 800 });
                          }} sx={{
                            p: 1.5, borderBottom: '1px solid', borderColor: 'divider',
                            display: 'flex', alignItems: 'flex-start', gap: 1.5, cursor: 'pointer',
                            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.05) },
                          }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#f44336', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontSize: 10, fontWeight: 'bold' }}>{ev.maxSpeed}</Box>
                            <Box>
                              <Typography variant="body2" fontWeight="bold" color="error.main">
                                Violation #{idx + 1} — {ev.maxSpeed} km/h
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {fmtTime(ev.startTime)} · {ev.duration}s
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                        {activeSegment.stops.length === 0 && activeSegment.speedingEvents.length === 0 && (
                          <Box sx={{ p: 3, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No events in this segment</Typography></Box>
                        )}
                      </Box>
                    )}
                </Collapse>
              </CardContent>
            </Card>
          </Grid>

          {/* Stats */}
          <Grid item xs={12} md={4}>
            <Card sx={{ boxShadow: 3, height: '100%' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {activeSegment ? `Segment ${activeSegment.segmentNumber} Stats` : 'Overall Stats'}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {(() => {
                  const s = activeSegment?.stats || playbackData.stats;
                  return (
                    <Grid container spacing={1.5}>
                      {[
                        { label: 'Duration',  value: fmtDuration(s.duration), color: 'info.main' },
                        { label: 'Distance',  value: `${s.distance} km`,      color: 'success.main' },
                        { label: 'Max Speed', value: `${s.maxSpeed} km/h`,    color: 'error.main' },
                        { label: 'Avg Speed', value: `${s.avgSpeed} km/h`,    color: 'warning.main' },
                        { label: 'Points',    value: s.pointCount,             color: 'secondary.main' },
                        {
                          label: 'Stops',
                          value: activeSegment
                            ? activeSegment.stops.length
                            : playbackData.segments.reduce((t, seg) => t + seg.stops.length, 0),
                          color: 'warning.main',
                        },
                      ].map(({ label, value, color }) => (
                        <Grid item xs={6} key={label}>
                          <Paper elevation={0} sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                            <Typography variant="h6" fontWeight="bold" color={color}>{value}</Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Playback Controls + Speed Graph */}
      {activeSegment && activeSegment.points.length > 0 && (
        <Paper sx={{ p: 3, mb: 2, boxShadow: 3 }}>

          {/* Controls row */}
          <Grid container spacing={3} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} md={5}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <IconButton onClick={isPlaying ? pausePlayback : startPlayback}
                  disabled={currentIndex >= currentPoints.length - 1} size="large"
                  sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}>
                  {isPlaying ? <Pause /> : <PlayArrow />}
                </IconButton>
                <IconButton onClick={stopPlayback} size="large"
                  sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
                  <Stop />
                </IconButton>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" mb={0.5}>Speed</Typography>
                  <ToggleButtonGroup value={playbackSpeed} exclusive onChange={(_, v) => v && handleSpeedChange(v)} size="small">
                    {[1, 5, 10, 25, 50].map(s => <ToggleButton key={s} value={s}>x{s}</ToggleButton>)}
                  </ToggleButtonGroup>
                </Box>
                {currentPoint && (
                  <>
                    <Chip icon={<SpeedIcon />} label={`${Math.round(currentPoint.speed)} km/h`}
                      color={currentPoint.speed > 80 ? 'error' : currentPoint.speed > 60 ? 'warning' : 'success'}
                      sx={{ fontWeight: 'bold' }} />
                    <Chip icon={<DirectionsCar />}
                      label={currentPoint.ignition ? 'ENGINE ON' : 'ENGINE OFF'}
                      color={currentPoint.ignition ? 'success' : 'default'} sx={{ fontWeight: 'bold' }} />
                  </>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  {currentPoint && fmtFull(currentPoint.timestamp)}
                </Typography>
                <Typography variant="caption" color="primary" fontWeight="bold">
                  Point {currentIndex + 1} of {currentPoints.length}
                </Typography>
              </Box>
              <Slider value={currentIndex} onChange={(_, v) => setCurrentIndex(v as number)}
                min={0} max={currentPoints.length - 1}
                sx={{
                  '& .MuiSlider-thumb': { width: 20, height: 20 },
                  '& .MuiSlider-track': { height: 6 },
                  '& .MuiSlider-rail': { height: 6, opacity: 0.3 },
                }}
              />
            </Grid>
          </Grid>

          {/* ── Speed Graph ──────────────────────────────────────────────── */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                Speed Profile — click anywhere to jump playback
              </Typography>
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1.5}>
                {[
                  { color: '#00e676', label: 'Normal' },
                  { color: '#ff9800', label: '60–80' },
                  { color: '#f44336', label: '>80 km/h' },
                  { color: '#ff9800', label: '🅿️ Stop' },
                ].map(({ color, label }) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box sx={{
              height: 150,
              bgcolor: alpha(theme.palette.background.default, 0.6),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              overflow: 'hidden',
              cursor: 'crosshair',
              transition: 'border 0.2s',
              '&:hover': { border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}` },
            }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={speedGraphData} onClick={handleChartClick}
                  margin={{ top: 10, right: 16, bottom: 4, left: 0 }}>
                  <defs>
                    <linearGradient id="speedAreaFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f44336" stopOpacity={0.85} />
                      <stop offset={`${Math.max(0, Math.min(100, 100 - (80 / maxGraphSpeed) * 100))}%`} stopColor="#f44336" stopOpacity={0.6} />
                      <stop offset={`${Math.max(0, Math.min(100, 100 - (80 / maxGraphSpeed) * 100))}%`} stopColor="#00e676" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#00e676" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="speedStroke" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f44336" />
                      <stop offset={`${Math.max(0, Math.min(100, 100 - (80 / maxGraphSpeed) * 100))}%`} stopColor="#f44336" />
                      <stop offset={`${Math.max(0, Math.min(100, 100 - (80 / maxGraphSpeed) * 100))}%`} stopColor="#00e676" />
                      <stop offset="100%" stopColor="#00e676" />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} vertical={false} />
                  <XAxis dataKey="time" tick={{ fontSize: 9, fill: theme.palette.text.disabled }}
                    interval="preserveStartEnd" axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: theme.palette.text.disabled }}
                    axisLine={false} tickLine={false} domain={[0, maxGraphSpeed]}
                    tickFormatter={(v) => `${v}`} width={28} />
                  <RechartsTooltip content={<SpeedGraphTooltip />} />

                  {/* 80 km/h limit line */}
                  <ReferenceLine y={80} stroke="#f44336" strokeDasharray="4 2"
                    strokeOpacity={0.8} strokeWidth={1.5}
                    label={{ value: '80 km/h', position: 'insideTopRight', fontSize: 9, fill: '#f44336' }}
                  />

                  {/* Stop dots on x-axis */}
                  {activeSegment.stops.map((stop, idx) => {
                    const chartPt = speedGraphData.find(d => Math.abs(d.pointIndex - stop.pointIndex) < 3);
                    if (!chartPt) return null;
                    return <ReferenceDot key={`stopdot-${idx}`} x={chartPt.time} y={0} r={5} fill="#ff9800" stroke="white" strokeWidth={1.5} />;
                  })}

                  {/* Speeding event dots */}
                  {activeSegment.speedingEvents.map((ev, idx) => {
                    const chartPt = speedGraphData.find(d => Math.abs(d.pointIndex - ev.pointIndex) < 3);
                    if (!chartPt) return null;
                    return <ReferenceDot key={`speeddot-${idx}`} x={chartPt.time} y={ev.maxSpeed} r={4} fill="#f44336" stroke="white" strokeWidth={1.5} />;
                  })}

                  {/* Current playback position */}
                  {speedGraphData[chartActiveIndex] && (
                    <ReferenceLine x={speedGraphData[chartActiveIndex].time}
                      stroke={theme.palette.primary.main} strokeWidth={2} strokeOpacity={0.95}
                      label={{ value: '▼', position: 'insideTopLeft', fontSize: 11, fill: theme.palette.primary.main }}
                    />
                  )}

                  <Area type="monotone" dataKey="speed"
                    stroke="url(#speedStroke)" strokeWidth={2}
                    fill="url(#speedAreaFill)" dot={false}
                    activeDot={{ r: 5, fill: theme.palette.primary.main, stroke: 'white', strokeWidth: 2 }}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Map */}
      <Card sx={{ boxShadow: 6, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ height: { xs: '70dvh', sm: 650 }, minHeight: 400, width: '100%', position: 'relative' }}>
          <div ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />

          {playbackData && (
            <Paper elevation={4} sx={{
              position: 'absolute', bottom: 24, left: { xs: 8, sm: 20 }, p: { xs: 1, sm: 2 }, maxHeight: { xs: 150, sm: 'none' }, overflowY: 'auto',
              bgcolor: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: 'blur(10px)', borderRadius: 2,
            }}>
              <Typography variant="caption" fontWeight="bold" display="block" mb={1}>Map Legend</Typography>
              <Stack spacing={1}>
                {[
                  { color: '#00ff00', label: 'Traveled Path', h: 6 },
                  { color: '#2196f3', label: 'Segment Route', h: 4 },
                  { color: '#555',    label: 'Full Route',    h: 3 },
                ].map(({ color, label, h }) => (
                  <Box key={label} display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 24, height: h, bgcolor: color, borderRadius: 1 }} />
                    <Typography variant="caption" fontWeight="600">{label}</Typography>
                  </Box>
                ))}
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#ff9800', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>🅿️</Box>
                  <Typography variant="caption" fontWeight="600">Stop</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#f44336', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 9, fontWeight: 'bold' }}>spd</Box>
                  <Typography variant="caption" fontWeight="600">Speed Violation</Typography>
                </Box>
              </Stack>
            </Paper>
          )}

          {playbackData && !activeSegment && (
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', pointerEvents: 'none' }}>
              <Paper elevation={6} sx={{ p: 3, bgcolor: 'rgba(0,0,0,0.7)', color: 'white', borderRadius: 3 }}>
                <RouteIcon sx={{ fontSize: 40, mb: 1, opacity: 0.7 }} />
                <Typography variant="h6">Select a segment to begin playback</Typography>
              </Paper>
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default TripPlaybackPage;